import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { TombstoneActions } from '../actions/tombstone/tombstone.actions';
import { ClientActions } from '../actions/client/client.actions';

import type { Tombstone } from '../interfaces/client/tombstone.interface';
import type { Client } from '../interfaces/client/client.interface';
import type { User } from '../interfaces/user/user.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import KvRow from './components/KvRow';

import TombstoneEditModal from './TombstoneEditModal';
import {
  formatZar,
  formatDate,
  formatMonthYear,
  yearOf,
  initialsFromName,
  deriveTombstoneType,
  deriveTombstoneRole
} from './lib/mandate-helpers';

// =============================================================================
//  Tombstone detail — v3 redesign
//
//  Read-only deal record.  A tombstone is BSM's track-record entry for a
//  closed transaction; the UI emphasises:
//    - The deal at a glance (year, client, role, value, fee)
//    - Engagement team (linked_people on the model)
//    - Sector + category positioning (chip pile)
//    - Related work (other tombstones for the same client or in the same
//      sub-category — built client-side from the global tombstones store)
//
//  Source jbuilder fields:
//    id, category, sub_category, date, description, linked_people[],
//    size_of_transaction, fee_of_transaction, client_id, client,
//    bsm_role, deal_type, created_at, updated_at
// =============================================================================

type TabKey = 'overview' | 'activity' | 'documents';

const TABS: ReadonlyArray<{ key: TabKey; label: string; ready: boolean }> = [
  { key: 'overview', label: 'Overview', ready: true },
  { key: 'activity', label: 'Activity', ready: false },
  { key: 'documents', label: 'Documents', ready: false }
];

const fullPersonName = (p: User): string =>
  (p as User & { full_name?: string }).full_name ||
  `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
  `#${p.id}`;

const TombstoneDetailV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const tombstones: Tombstone[] =
    useAppSelector(({ tombstone_store }) => tombstone_store.tombstones) || [];
  const clients: Client[] = useAppSelector(({ client_store }) => client_store.clients) || [];

  const [tab, setTab] = useState<TabKey>('overview');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    dispatch(TombstoneActions.loadTombstones(noOp));
    // Pull clients so we can offer a deep link from the right rail to the
    // related client page when a tombstone has client_id set.
    if (clients.length === 0) dispatch(ClientActions.loadClients(noOp));
  }, [dispatch, clients.length]);

  const tombstone = useMemo(
    () => tombstones.find((t) => String(t.id) === String(id)),
    [tombstones, id]
  );

  // Related tombstones (same client OR same sub-category), excluding this one.
  const related = useMemo(() => {
    if (!tombstone) return [] as Tombstone[];
    const same = (t: Tombstone) =>
      (tombstone.client_id && String(t.client_id) === String(tombstone.client_id)) ||
      (tombstone.sub_category && t.sub_category === tombstone.sub_category) ||
      (tombstone.category && t.category === tombstone.category);
    return tombstones
      .filter((t) => String(t.id) !== String(tombstone.id) && same(t))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .slice(0, 6);
  }, [tombstone, tombstones]);

  // Loading / not-found guards (after all hooks have run).
  if (!tombstone && tombstones.length === 0) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Tombstones', '…']} />
        <div className="page">
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Loading tombstone…
          </div>
        </div>
      </>
    );
  }
  if (!tombstone) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Tombstones', 'Not found']} />
        <div className="page">
          <button className="back-link" type="button" onClick={() => navigate('/tombstones')}>
            <Icon name="back" size={14} />
            All tombstones
          </button>
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Tombstone <strong>#{id}</strong> not found.
          </div>
        </div>
      </>
    );
  }

  // Derived display ---------------------------------------------------------
  const year = yearOf(tombstone.date);
  const dealType = deriveTombstoneType(tombstone);
  const bsmRole = deriveTombstoneRole(tombstone);
  const linkedPeople: User[] = Array.isArray(tombstone.linked_people)
    ? (tombstone.linked_people as User[]).filter(
        (p): p is User => typeof p === 'object' && p !== null && 'id' in p
      )
    : [];
  const clientName = tombstone.client || 'Unnamed deal';
  // Title line: prefer description's first line as the "project" name,
  // fall back to client + role.
  const projectTitle = (() => {
    if (tombstone.description) {
      const firstLine = tombstone.description.split('\n')[0].trim();
      if (firstLine && firstLine.length > 0) {
        return firstLine.length > 80 ? `${firstLine.slice(0, 80)}…` : firstLine;
      }
    }
    return clientName;
  })();
  const hasDescription =
    typeof tombstone.description === 'string' && tombstone.description.trim().length > 0;
  const relatedClient = clients.find((c) => String(c.id) === String(tombstone.client_id));

  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Tombstones', clientName]}
        tools={
          <>
            <button className="btn ghost" type="button" onClick={() => navigate('/tombstones')}>
              <Icon name="back" size={13} />
              All tombstones
            </button>
            <button className="btn primary" type="button" onClick={() => setEditing(true)}>
              <Icon name="check" size={13} />
              Edit
            </button>
          </>
        }
      />
      {editing && <TombstoneEditModal tombstone={tombstone} onClose={() => setEditing(false)} />}
      <div className="page">
        {/* --- Hero card -------------------------------------------------- */}
        <div className="detail-hero">
          <div className="detail-hero-top">
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 6,
                background: 'var(--ink)',
                color: 'var(--gold-soft)',
                display: 'inline-grid',
                placeItems: 'center',
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 500,
                letterSpacing: 1
              }}
              aria-hidden
            >
              {initialsFromName(clientName)}
            </div>
            <div className="meta">
              <div className="chips">
                <span className="chip muted mono">{String(year)}</span>
                <span className="chip gold">{dealType}</span>
                {tombstone.sub_category && (
                  <span className="chip muted">{tombstone.sub_category}</span>
                )}
              </div>
              <h1>{projectTitle}</h1>
              <div className="sub">
                {[clientName, bsmRole, formatMonthYear(tombstone.date)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="value">
              <div className="eyebrow">Transaction value</div>
              <div className="v">{formatZar(tombstone.size_of_transaction)}</div>
              <div className="sub">{formatDate(tombstone.date) || '—'}</div>
            </div>
          </div>

          <div className="detail-hero-stats">
            <div className="stat">
              <div className="label">Fee of transaction</div>
              <div className="value">{formatZar(tombstone.fee_of_transaction)}</div>
            </div>
            <div className="stat">
              <div className="label">Category</div>
              <div className="value" style={{ fontSize: 16 }}>
                {tombstone.category || '—'}
              </div>
            </div>
            <div className="stat">
              <div className="label">Linked people</div>
              <div className="value">{linkedPeople.length}</div>
            </div>
            <div className="stat">
              <div className="label">Logged</div>
              <div className="value mono" style={{ fontSize: 14 }}>
                {formatDate(tombstone.created_at) || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* --- Tabs ------------------------------------------------------- */}
        <div className="tab-strip">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab ${tab === t.key ? 'active' : ''} ${!t.ready ? 'stub' : ''}`}
              onClick={() => setTab(t.key)}
              title={t.ready ? undefined : 'Coming soon — backend wiring pending'}
            >
              {!t.ready && (
                <span aria-hidden style={{ opacity: 0.4 }}>
                  ·&nbsp;
                </span>
              )}
              {t.label}
            </button>
          ))}
        </div>

        {/* --- Overview --------------------------------------------------- */}
        {tab === 'overview' && (
          <div className="grid detail">
            {/* LEFT column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              {hasDescription && (
                <div className="card">
                  <h3
                    style={{
                      margin: '0 0 14px 0',
                      fontFamily: 'var(--font-display)',
                      fontSize: 18,
                      fontWeight: 500,
                      color: 'var(--ink)'
                    }}
                  >
                    Description
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-body)',
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: 'var(--ink-2)',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {tombstone.description}
                  </p>
                </div>
              )}

              <div className="card flush">
                <div className="card-h">
                  <h3>Related deals</h3>
                </div>
                {related.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                  >
                    No related tombstones found for{' '}
                    {tombstone.client
                      ? clientName
                      : tombstone.sub_category || tombstone.category || 'this record'}
                    .
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Client</th>
                        <th>Type</th>
                        <th>Sector</th>
                        <th className="right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {related.map((t) => (
                        <tr
                          key={t.id}
                          onClick={() => navigate(`/tombstones/${t.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="mono">{yearOf(t.date)}</td>
                          <td className="strong">{t.client || '—'}</td>
                          <td>
                            <span className="chip gold">{deriveTombstoneType(t)}</span>
                          </td>
                          <td>{t.sub_category || t.category || '—'}</td>
                          <td className="num right">{formatZar(t.size_of_transaction)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* RIGHT column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Engagement team
                </h3>
                {linkedPeople.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    No personnel linked to this tombstone.
                  </div>
                ) : (
                  linkedPeople.map((p) => (
                    // Rows are clickable through to the employee detail
                    // page so users can jump from a deal to the person
                    // who worked on it.
                    <div
                      className="team-row"
                      key={p.id}
                      onClick={() => p.id && navigate(`/employees/${p.id}`)}
                      role={p.id ? 'button' : undefined}
                      tabIndex={p.id ? 0 : undefined}
                      onKeyDown={(ev) => {
                        if (p.id && (ev.key === 'Enter' || ev.key === ' ')) {
                          ev.preventDefault();
                          navigate(`/employees/${p.id}`);
                        }
                      }}
                      style={{ cursor: p.id ? 'pointer' : undefined }}
                    >
                      <div className="av">{initialsFromName(fullPersonName(p))}</div>
                      <div className="who">
                        <span className="name">{fullPersonName(p)}</span>
                        {p.role && <span className="role">{p.role.replace('_', ' ')}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Key details
                </h3>
                <KvRow
                  label="Client"
                  value={
                    relatedClient ? (
                      <button
                        type="button"
                        className="back-link"
                        onClick={() => navigate(`/clients/${relatedClient.id}`)}
                        style={{ padding: 0 }}
                      >
                        {clientName}
                      </button>
                    ) : (
                      clientName
                    )
                  }
                />
                <KvRow label="Category" value={tombstone.category || '—'} />
                <KvRow label="Sub-category" value={tombstone.sub_category || '—'} />
                <KvRow label="Deal type" value={dealType} />
                <KvRow label="BSM role" value={bsmRole} />
                <KvRow label="Date" value={formatDate(tombstone.date) || '—'} mono />
                <KvRow
                  label="Size of transaction"
                  value={formatZar(tombstone.size_of_transaction)}
                  mono
                />
                <KvRow
                  label="Fee of transaction"
                  value={formatZar(tombstone.fee_of_transaction)}
                  mono
                />
              </div>
            </div>
          </div>
        )}

        {/* --- Coming-soon panes (stubbed tabs) --------------------------- */}
        {tab !== 'overview' && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Coming soon
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>
              {TABS.find((t) => t.key === tab)?.label}
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              {tab === 'activity' && (
                <>
                  Tombstones are versioned via paper_trail on the backend, but the per-tombstone
                  activity / version-history endpoint isn't wired yet.
                </>
              )}
              {tab === 'documents' && (
                <>
                  Tombstones don't have their own document attachments today; related deal-sheet
                  PDFs live on the deal_sheets model. Surfacing them here is a future enhancement.
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TombstoneDetailV3;
